import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { CiSearch } from 'react-icons/ci';
import Page from 'src/components/Page';
import CustomPagination from 'src/components/CustomPagination';
import palette from 'src/theme/palette';
import { RoleDefinition } from 'src/types/permissions.types';
import { RoleMember } from 'src/services/role.service';
import { RoleCard } from 'src/components/roles/RoleCard';
import { CreateRoleModal } from 'src/components/roles/CreateRoleModal';
import { AssignRoleModal } from 'src/components/roles/AssignRoleModal';
import { DeleteRoleModal } from 'src/components/roles/DeleteRoleModal';
import {
  EmptyState,
  FilterChip,
  personName,
  primaryButtonSx,
  roleAccent,
  secondaryButtonSx,
  ShellChip,
  StatCard,
} from 'src/components/roles/RoleUi';
import { useRolesAdmin } from './useRolesAdmin';

type RoleFilter = 'ALL' | 'BUILT_IN' | 'CUSTOM';

const tableHeadSx = { py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' } as const;

const filterBarSx = {
  p: 1.5,
  mb: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  bgcolor: 'background.neutral',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { xs: 'stretch', md: 'center' },
  justifyContent: 'space-between',
  gap: 1.5,
} as const;

function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': placeholder }}
      sx={{ width: { xs: '100%', md: 320 } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CiSearch size={18} color="#94A3B8" />
          </InputAdornment>
        ),
        sx: { height: 36, bgcolor: 'background.paper', borderRadius: 1.5, fontSize: '0.8125rem', maxWidth: { xs: '100%', md: 320 } },
      }}
    />
  );
}

export default function Roles() {
  const navigate = useNavigate();
  const { roles, members, membersOf, isLoading, error, createRole, deleteRole, assignRole } = useRolesAdmin();

  const [tab, setTab] = useState(0);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peopleRole, setPeopleRole] = useState('ALL');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<{ member: RoleMember | null } | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<RoleDefinition | null>(null);

  const roleLabel = (name?: string | null) => roles.find((r) => r.name === name)?.label || name || 'No role';
  const openRole = (role: RoleDefinition) => navigate(`/admin/roles/${role.name}`);

  const visibleRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    return roles.filter((r) => {
      if (roleFilter === 'BUILT_IN' && !r.is_system) return false;
      if (roleFilter === 'CUSTOM' && r.is_system) return false;
      return !q || [r.label, r.name, r.description || ''].some((text) => text.toLowerCase().includes(q));
    });
  }, [roles, roleSearch, roleFilter]);

  const visiblePeople = useMemo(() => {
    const q = peopleSearch.trim().toLowerCase();
    return members.filter((m) => {
      if (peopleRole !== 'ALL' && (m.role || '').toUpperCase() !== peopleRole) return false;
      return !q || personName(m).toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [members, peopleSearch, peopleRole]);

  const pagedPeople = visiblePeople.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const customCount = roles.filter((r) => !r.is_system).length;
  const bankerCount = membersOf('BANKER').length;

  return (
    <Page title="Roles & Permissions | Gold Minds Portal">
      <Box sx={{ width: '100%', px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Ribbon */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: alpha(palette.primary.main, 0.12),
                  color: palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="solar:shield-user-bold-duotone" width={22} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                  Roles & Permissions
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Decide who can do what across the Gold Minds economy, create roles such as Dean, and appoint Student Bankers.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon icon="solar:user-plus-bold" width={16} />}
                onClick={() => setAssignTarget({ member: null })}
                sx={secondaryButtonSx}
              >
                Assign Role
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                onClick={() => setIsCreateOpen(true)}
                sx={primaryButtonSx}
              >
                New Role
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <StatCard label="Roles" value={roles.length} caption="Built-in and custom" icon="solar:shield-user-bold-duotone" color="#2563EB" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Custom Roles" value={customCount} caption="Created by your school" icon="solar:magic-stick-3-bold-duotone" color="#7C3AED" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              label="People"
              value={members.length}
              caption="Accounts with a role"
              icon="solar:users-group-rounded-bold-duotone"
              color="#059669"
              valueColor="#059669"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Student Bankers" value={bankerCount} caption="Trusted scholars" icon="solar:wallet-money-bold-duotone" color="#D97706" />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 44,
                py: 1,
                px: 2,
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
              },
            }}
          >
            <Tab icon={<Icon icon="solar:shield-user-bold-duotone" width={18} />} iconPosition="start" label={`Roles Directory (${roles.length})`} />
            <Tab icon={<Icon icon="solar:users-group-rounded-bold-duotone" width={18} />} iconPosition="start" label={`People & Access (${members.length})`} />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load roles. Please check your connection.
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size={32} />
            <Typography variant="caption" sx={{ mt: 1.5, color: 'text.secondary', fontWeight: 500 }}>
              Loading roles...
            </Typography>
          </Box>
        ) : tab === 0 ? (
          <Box>
            <Card sx={filterBarSx}>
              <SearchField value={roleSearch} onChange={setRoleSearch} placeholder="Search roles by name or purpose..." />
              <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', py: 0.25 }}>
                <FilterChip label="All Roles" active={roleFilter === 'ALL'} onClick={() => setRoleFilter('ALL')} />
                <FilterChip label="Built-in" active={roleFilter === 'BUILT_IN'} onClick={() => setRoleFilter('BUILT_IN')} />
                <FilterChip label={`Custom (${customCount})`} active={roleFilter === 'CUSTOM'} onClick={() => setRoleFilter('CUSTOM')} />
              </Stack>
            </Card>

            {visibleRoles.length === 0 ? (
              <EmptyState
                icon="solar:shield-user-bold-duotone"
                title={roleFilter === 'CUSTOM' && !roleSearch ? 'No custom roles yet' : 'No matching roles'}
                message={
                  roleFilter === 'CUSTOM' && !roleSearch
                    ? 'Create a role such as Dean to give staff exactly the access they need.'
                    : 'Try a different search or filter.'
                }
                actionLabel="Create a Role"
                onAction={roleFilter === 'CUSTOM' && !roleSearch ? () => setIsCreateOpen(true) : undefined}
              />
            ) : (
              <Grid container spacing={2}>
                {visibleRoles.map((role) => (
                  <Grid item xs={12} sm={6} lg={4} key={role.name}>
                    <RoleCard
                      role={role}
                      members={membersOf(role.name)}
                      onOpen={() => openRole(role)}
                      onDelete={role.is_system ? undefined : () => setRoleToDelete(role)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ) : (
          <Box>
            <Card sx={filterBarSx}>
              <SearchField
                value={peopleSearch}
                onChange={(v) => {
                  setPeopleSearch(v);
                  setPage(1);
                }}
                placeholder="Search people by name or email..."
              />
              <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', py: 0.25 }}>
                <FilterChip
                  label="Everyone"
                  active={peopleRole === 'ALL'}
                  onClick={() => {
                    setPeopleRole('ALL');
                    setPage(1);
                  }}
                />
                {roles.map((r) => (
                  <FilterChip
                    key={r.name}
                    label={`${r.label} (${membersOf(r.name).length})`}
                    active={peopleRole === r.name}
                    onClick={() => {
                      setPeopleRole(r.name);
                      setPage(1);
                    }}
                  />
                ))}
              </Stack>
            </Card>

            {visiblePeople.length === 0 ? (
              <EmptyState icon="solar:users-group-rounded-bold-duotone" title="No matching people" message="Try a different search or role filter." />
            ) : (
              <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell sx={tableHeadSx}>Person</TableCell>
                        <TableCell sx={tableHeadSx}>Role</TableCell>
                        <TableCell sx={{ ...tableHeadSx, display: { xs: 'none', md: 'table-cell' } }}>Dashboard</TableCell>
                        <TableCell align="right" sx={tableHeadSx}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedPeople.map((m) => {
                        const role = roles.find((r) => r.name === (m.role || '').toUpperCase());
                        const color = role ? roleAccent(role).color : '#94A3B8';
                        return (
                          <TableRow key={m.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ py: 1.25 }}>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Avatar sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700, bgcolor: alpha(color, 0.12), color }}>
                                  {(m.firstname || m.email)[0]?.toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight={700} color="#0F172A" fontSize="0.8125rem" noWrap>
                                    {personName(m)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" noWrap display="block">
                                    {m.email}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <Chip
                                label={roleLabel(role?.name || m.role)}
                                size="small"
                                onClick={role ? () => openRole(role) : undefined}
                                sx={{ height: 24, fontWeight: 700, fontSize: '0.72rem', bgcolor: alpha(color, 0.1), color }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 1.25, display: { xs: 'none', md: 'table-cell' } }}>
                              {role ? <ShellChip role={role} /> : <Typography variant="caption">Unknown role</Typography>}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setAssignTarget({ member: m })}
                                sx={{
                                  borderRadius: 1.5,
                                  borderColor: '#CBD5E1',
                                  color: '#334155',
                                  fontWeight: 700,
                                  fontSize: '0.725rem',
                                  textTransform: 'none',
                                  '&:hover': { bgcolor: '#F8FAFC' },
                                }}
                              >
                                Change Role
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <CustomPagination
                    count={visiblePeople.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={setPage}
                    onRowsPerPageChange={(n) => {
                      setRowsPerPage(n);
                      setPage(1);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Box>
              </Card>
            )}
          </Box>
        )}

        <CreateRoleModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          roles={roles}
          isSubmitting={createRole.isPending}
          onSubmit={async (input) => {
            const role = await createRole.mutateAsync(input);
            navigate(`/admin/roles/${role.name}`);
          }}
        />

        <AssignRoleModal
          open={Boolean(assignTarget)}
          onClose={() => setAssignTarget(null)}
          members={members}
          roles={roles}
          initialMember={assignTarget?.member}
          isSubmitting={assignRole.isPending}
          onSubmit={(userId, roleName) => assignRole.mutateAsync({ userId, roleName })}
        />

        <DeleteRoleModal
          open={Boolean(roleToDelete)}
          role={roleToDelete}
          memberCount={roleToDelete ? membersOf(roleToDelete.name).length : 0}
          onClose={() => setRoleToDelete(null)}
          isDeleting={deleteRole.isPending}
          onConfirm={() =>
            roleToDelete
              ? deleteRole
                  .mutateAsync(roleToDelete)
                  .then(() => setRoleToDelete(null))
                  .catch(() => undefined)
              : Promise.resolve()
          }
        />
      </Box>
    </Page>
  );
}
